import { Box, Flex, Text, useColorModeValue } from '@chakra-ui/react';
import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import Card from '../../../components/card/Card';
import { ChurnDistributionDto } from '../../../models/churn-prediction/dto/churn-prediction.dto';
import { translate } from '../../../i18n';
import { CHURN_CHART_COLORS } from '../utils/churn.utils';

interface Props {
  distribution: ChurnDistributionDto;
}

export default function ChurnDistributionChart({ distribution }: Props) {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const tooltipTheme = useColorModeValue('light', 'dark');

  const series = useMemo(
    () => [distribution.safe, distribution.watch, distribution.atRisk, distribution.critical],
    [distribution],
  );

  const options: ApexCharts.ApexOptions = useMemo(
    () => ({
      chart: { type: 'donut' as const },
      labels: [
        translate('churn_prediction.tier_safe'),
        translate('churn_prediction.tier_watch'),
        translate('churn_prediction.tier_at_risk'),
        translate('churn_prediction.tier_critical'),
      ],
      colors: CHURN_CHART_COLORS,
      legend: {
        position: 'bottom' as const,
        labels: { colors: textColor },
      },
      dataLabels: { enabled: true },
      plotOptions: {
        pie: {
          donut: {
            size: '60%',
            labels: {
              show: true,
              total: {
                show: true,
                label: translate('churn_prediction.total'),
              },
            },
          },
        },
      },
      tooltip: {
        theme: tooltipTheme,
      },
    }),
    [textColor, tooltipTheme],
  );

  return (
    <Card alignItems="center" flexDirection="column" w="100%">
      <Flex align="center" w="100%" px="15px" py="10px">
        <Text me="auto" color={textColor} fontSize="xl" fontWeight="700" lineHeight="100%">
          {translate('churn_prediction.score_distribution')}
        </Text>
      </Flex>
      <Box h="350px" w="100%">
        <ReactApexChart options={options} series={series} type="donut" height={350} />
      </Box>
    </Card>
  );
}
